import {
  SelectFilter,
  SelectFilterPaginationProps,
  SelectFilterProps,
} from 'components/Filters/fields/SelectFilter'
import { TableTypeSlug } from 'models/companies'
import { useEffect, useMemo, useState } from 'react'
import useApiService from 'services/api'
import { CityStatesPaginatedResponse } from 'services/common_table_actions'
import { US_CITIES } from 'utils/us_cities'
import { US_STATES } from '../../../utils/us_states'

import { FilterStore } from '../FilterStore'

export function CityAndStateFilter({
  filterStore,
  stateSelectProps,
  citySelectProps,
  cityKey = 'city',
  stateKey = 'state',
  allAvailableCities = US_CITIES, // Allow passing in a custom list of cities for testing or other purposes
  hideStateFilter = false,
  companyType, // When provided, enables pagination mode with server-side fetching
}: {
  filterStore: FilterStore
  stateSelectProps: Omit<SelectFilterProps, 'filterStore'>
  citySelectProps: Omit<SelectFilterProps, 'filterStore'>
  cityKey?: string
  stateKey?: string
  allAvailableCities?: { value: string; label: string }[] // For testing or custom city lists
  hideStateFilter?: boolean // Hide state filter if needed
  companyType?: TableTypeSlug // When provided, enables pagination mode
}) {
  const { pendingValueFilters, setPendingValueFilters } = filterStore()
  const isPaginated = !!companyType

  // State for server-side search in paginated mode
  const [citySearch, setCitySearch] = useState('')

  // Get selected states for filtering
  const selectedStates = useMemo(
    () => pendingValueFilters[stateKey]?.map((f) => f.value) ?? [],
    [pendingValueFilters, stateKey]
  )

  // Paginated city data fetching
  const api = useApiService()
  const {
    data: paginatedCityData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = api.useCommonGetCityStatesInfinite(
    companyType ?? 'restaurants-bars',
    {
      search: citySearch,
      state: selectedStates.join(','),
      page_size: 100,
    },
    isPaginated
  )

  // Flatten paginated results
  const paginatedCities = useMemo(() => {
    if (!paginatedCityData?.pages) return []
    return paginatedCityData.pages.flatMap(
      (page: CityStatesPaginatedResponse) => page.results
    )
  }, [paginatedCityData])

  // Non-paginated mode: client-side state
  const [usCities, setUsCities] = useState<{ value: string; label: string }[]>()

  // Non-paginated mode: filter cities by selected states client-side
  useEffect(() => {
    if (isPaginated) return // Skip for paginated mode

    if (!selectedStates?.length) {
      setUsCities(allAvailableCities)
      return
    }

    const availableCities = allAvailableCities.filter((c) =>
      selectedStates.some((s) => s === c.value.split('_')[1])
    )

    // Use cityKey to get the selected cities
    const selectedCities = pendingValueFilters[cityKey]?.map((f) => f.value)

    if (selectedCities) {
      const citiesToRemove: string[] = []
      for (const city of selectedCities) {
        if (!availableCities.some((c) => c.value === city)) {
          citiesToRemove.push(city as string)
        }
      }
      if (citiesToRemove.length) {
        setPendingValueFilters(
          cityKey,
          pendingValueFilters[cityKey]?.filter(
            (f) => !citiesToRemove.some((c) => c === f.value)
          )
        )
      }
    }

    setUsCities(availableCities)
  }, [pendingValueFilters, allAvailableCities, isPaginated])

  // Pagination props for the city SelectFilter
  const cityPaginationProps: SelectFilterPaginationProps | undefined =
    isPaginated
      ? {
          onLoadMore: () => fetchNextPage(),
          hasMore: !!hasNextPage,
          isLoadingMore: isFetchingNextPage,
          onSearchChange: setCitySearch,
          totalCount: (
            paginatedCityData?.pages[0] as
              | CityStatesPaginatedResponse
              | undefined
          )?.count,
        }
      : undefined

  // Choose city options based on mode
  const cityOptions = isPaginated ? paginatedCities : usCities

  return (
    <>
      {!hideStateFilter && (
        <SelectFilter
          {...stateSelectProps}
          options={US_STATES}
          filterStore={filterStore}
        />
      )}
      <SelectFilter
        {...citySelectProps}
        options={cityOptions}
        filterStore={filterStore}
        pagination={cityPaginationProps}
      />
    </>
  )
}

// CityAndStateFilter renders two SelectFilter triggers side-by-side. In
// alphabetical mode, FilterModal can't sort them independently because they
// share a single composite element — bucket the pair under "City & State".
CityAndStateFilter.sortMeta = { title: 'City & State' }
